import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { API_URLS } from "../../config";
import { apiRequest } from "../../utils/apiRequest";
import AccessDenied from "../../Components/AccessDenied";
import { useToast } from "../../Components/Toast";
import {
  FiEdit, FiPlus, FiEye, FiTrash2, FiChevronUp, FiChevronDown, FiRefreshCw, FiCheck, FiX, FiUploadCloud, FiUser, FiMapPin, FiUsers
} from "react-icons/fi";
import MasterUploadModal from "../../Components/MasterUploadModal";
import PhoneField from "../../Components/PhoneField";
import ServiceSelection from "../../Components/ServiceSelection";
import Select from "react-select";
import { Country, State, City } from "country-state-city";
import "./Customers.css";
import { FaUserTie } from "react-icons/fa6";
import FilterPanel, { FilterField, filterSelectStyles } from "../../Components/FilterPanel";

const EMPTY_FORM = {
  customerName: "",
  customerCode: "",
  customerType: "Direct",
  country: "",
  state: "",
  city: "",
  streetAddress: "",
  email: "",
  contactNumber: "",
  gstVatNumber: "",
  services: [],
  coordinators: [],
  status: "Active",
};

const EMPTY_COORDINATOR = { name: "", email: "", mobileNumber: "" };

const initials = (name = "") =>
  name.trim().split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");

const Customers = () => {
  const { token, user } = useAuth();
  const toast = useToast();

  /* ── list state ── */
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  /* ── selection ── */
  const [selectedIds, setSelectedIds] = useState([]);

  /* ── filters ── */
  const [search, setSearch] = useState("");
  const [fCountry, setFCountry] = useState("");
  const [fState, setFState] = useState("");
  const [fStatus, setFStatus] = useState("");

  /* ── sort ── */
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  /* ── modal ── */
  const [modal, setModal] = useState(null); // "add" | "edit" | "view"
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isCoordUploadOpen, setIsCoordUploadOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  /* ── section collapse ── */
  const [secBasic, setSecBasic] = useState(true);
  const [secLocation, setSecLocation] = useState(true);
  const [secServices, setSecServices] = useState(true);
  const [secCoord, setSecCoord] = useState(true);
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);

  /* ── coordinator add form ── */
  const [coordFormOpen, setCoordFormOpen] = useState(false);
  const [coordinatorInput, setCoordinatorInput] = useState(EMPTY_COORDINATOR);

  /* ── country/state/city options ── */
  const allCountries = useMemo(() => Country.getAllCountries(), []);

  const selectedCountryIso = useMemo(() => {
    const c = allCountries.find((c) => c.name === formData.country);
    return c ? c.isoCode : "";
  }, [allCountries, formData.country]);

  const stateOptions = useMemo(() => {
    if (!selectedCountryIso) return [];
    return State.getStatesOfCountry(selectedCountryIso);
  }, [selectedCountryIso]);

  const selectedStateIso = useMemo(() => {
    const s = stateOptions.find((s) => s.name === formData.state);
    return s ? s.isoCode : "";
  }, [stateOptions, formData.state]);

  const cityOptions = useMemo(() => {
    if (!selectedCountryIso || !selectedStateIso) return [];
    return City.getCitiesOfState(selectedCountryIso, selectedStateIso);
  }, [selectedCountryIso, selectedStateIso]);

  /* filter-panel options — states/cities for whatever country/state is currently picked */
  const isoOfCountry = (name) => allCountries.find((c) => c.name === name)?.isoCode || "";
  const statesOf = (country) => {
    const iso = isoOfCountry(country);
    return iso ? State.getStatesOfCountry(iso) : [];
  };

  /* ── react-select styles — all corners 0 ── */
  const rsStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 30,
      height: 30,
      borderRadius: 0,
      border: state.isFocused ? "0.5px solid #378ADD" : "0.5px solid #ccc",
      backgroundColor: "var(--bg-primary, #fff)",
      boxShadow: "none",
      fontSize: 12,
      "&:hover": { border: "0.5px solid #999" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 8px" }),
    menu: (base) => ({ ...base, borderRadius: 0, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      fontSize: 12,
      backgroundColor: state.isSelected ? "#0C447C" : state.isFocused ? "#E6F1FB" : "#fff",
      color: state.isSelected ? "#E6F1FB" : "#111",
    }),
    multiValue: (base) => ({ ...base, borderRadius: 0, background: "#E6F1FB" }),
    multiValueLabel: (base) => ({ ...base, fontSize: 11, color: "#0C447C" }),
    multiValueRemove: (base) => ({ ...base, borderRadius: 0, ":hover": { background: "#F09595", color: "#791F1F" } }),
    placeholder: (base) => ({ ...base, fontSize: 12, color: "#aaa" }),
    singleValue: (base) => ({ ...base, fontSize: 12 }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 6px" }),
  };

  /* ── fetch ── */
  const fetchMasterData = useCallback(async () => {
    if (!token) return;
    const result = await apiRequest(`${API_URLS.CUSTOMERS}/options/master-data`, {}, token);
    if (result?.ok) { /* masterData reserved for future use */ }
  }, [token]);

  const fetchRows = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    const params = new URLSearchParams({ page, limit });
    if (search) params.set("search", search);
    if (fCountry) params.set("country", fCountry);
    if (fState) params.set("state", fState);
    if (fStatus) params.set("status", fStatus);
    const result = await apiRequest(`${API_URLS.CUSTOMERS}?${params}`, {}, token);
    if (!result) return;
    if (result.denied) { setDenied(result); setLoading(false); return; }
    if (result.ok) {
      setRows(result.data.rows || []);
      setTotal(result.data.total || 0);
      setSelectedIds([]);
    }
    setLoading(false);
  }, [token, page, search, fCountry, fState, fStatus, limit]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { if (modal) fetchMasterData(); }, [modal, fetchMasterData]);

  /* ── selection ── */
  const toggleSelectRow = (id) =>
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleSelectAll = () =>
    setSelectedIds(selectedIds.length === rows.length && rows.length > 0 ? [] : rows.map((r) => r.id));

  /* ── modal helpers ── */
  const initialFormSnapshotRef = useRef(null);
  const openAdd = () => {
    setFormData(EMPTY_FORM);
    initialFormSnapshotRef.current = JSON.stringify(EMPTY_FORM);
    setCoordinatorInput(EMPTY_COORDINATOR);
    setCoordFormOpen(false);
    setSecBasic(true); setSecLocation(true); setSecServices(true); setSecCoord(true);
    setModal("add");
  };

  const openEdit = async () => {
    if (selectedIds.length !== 1) return;
    const res = await apiRequest(`${API_URLS.CUSTOMERS}/${selectedIds[0]}`, {}, token);
    if (res?.ok) {
      setFormData(res.data);
      initialFormSnapshotRef.current = JSON.stringify(res.data);
      setCoordFormOpen(false);
      setModal("edit");
    }
  };

  const openView = async () => {
    if (selectedIds.length !== 1) return;
    const res = await apiRequest(`${API_URLS.CUSTOMERS}/${selectedIds[0]}`, {}, token);
    if (res?.ok) { setFormData(res.data); setCoordFormOpen(false); setModal("view"); }
  };

  const closeModal = () => {
    setModal(null);
    setFormData(EMPTY_FORM);
    setCoordinatorInput(EMPTY_COORDINATOR);
    setCoordFormOpen(false);
  };

  // Unsaved-changes guard — covers both the main form and an open-but-unsaved coordinator entry
  const isFormDirty = () => {
    if (modal === "view") return false;
    const coordDraftPending = coordFormOpen && !!(coordinatorInput.name || coordinatorInput.email || coordinatorInput.mobileNumber);
    const formChanged = initialFormSnapshotRef.current !== null && JSON.stringify(formData) !== initialFormSnapshotRef.current;
    return coordDraftPending || formChanged;
  };
  const requestCloseModal = () => {
    if (isFormDirty()) {
      toast.confirm(
        "You have unsaved changes. Do you want to save before closing?",
        () => handleSave(),
        () => closeModal()
      );
    } else {
      closeModal();
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    
    // For customerCode, allow only 3 digits
    if (name === "customerCode") {
      finalValue = value.replace(/[^0-9]/g, "").slice(0, 3);
    }
    
    setFormData((f) => ({ ...f, [name]: finalValue }));
  };

  /* ── coordinator logic ── */
  const openCoordForm = () => {
    setCoordinatorInput(EMPTY_COORDINATOR);
    setCoordFormOpen(true);
  };
  const closeCoordForm = () => {
    setCoordFormOpen(false);
    setCoordinatorInput(EMPTY_COORDINATOR);
  };
  const handleAddCoordinator = () => {
    if (!coordinatorInput.name || !coordinatorInput.email) {
      toast.warning("Name and email are required");
      return;
    }
    if (formData.coordinators.some((c) => c.email.toLowerCase() === coordinatorInput.email.toLowerCase())) {
      toast.warning("This email is already added");
      return;
    }
    setFormData((f) => ({ ...f, coordinators: [...f.coordinators, { ...coordinatorInput }] }));
    closeCoordForm();
  };
  const handleRemoveCoordinator = (index) =>
    setFormData((f) => ({ ...f, coordinators: f.coordinators.filter((_, i) => i !== index) }));


  /* ── save ── */
  const handleSave = async (e) => {
    e?.preventDefault();
    if (!formData.customerName || !formData.customerCode) {
      toast.warning("Customer Name and Customer Code are required!");
      return;
    }
    setSaving(true);
    const isEdit = modal === "edit";
    const url = isEdit ? `${API_URLS.CUSTOMERS}/${selectedIds[0]}` : API_URLS.CUSTOMERS;
    const result = await apiRequest(url, { method: isEdit ? "PUT" : "POST", body: formData }, token);
    setSaving(false);
    if (result?.ok) { toast.success("Saved successfully!"); closeModal(); fetchRows(); }
    else toast.error(result?.data?.message || "Failed");
  };

  /* ── delete ── */
  const handleDelete = () => {
    if (!selectedIds.length) return;
    const msg = selectedIds.length === 1 ? "Delete this customer?" : `Delete ${selectedIds.length} customers?`;
    toast.deleteConfirm(msg, async () => {
      setLoading(true);
      let ok = 0;
      for (const id of selectedIds) {
        const res = await apiRequest(`${API_URLS.CUSTOMERS}/${id}`, { method: "DELETE" }, token);
        if (res?.ok) ok++;
      }
      setLoading(false);
      if (ok > 0) { toast.success(`Deleted ${ok} item(s).`); fetchRows(); }
      else toast.error("Delete failed.");
    });
  };

  /* ── export ── */
  const handleExport = async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (fCountry) params.set("country", fCountry);
    if (fState) params.set("state", fState);
    if (fStatus) params.set("status", fStatus);
    const res = await fetch(`${API_URLS.CUSTOMERS}/export?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Customers_${Date.now()}.xlsx`; a.click();
    } else toast.error("Export failed");
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortField) return rows;
    return [...rows].sort((a, b) => {
      const av = (a[sortField] || "").toString().toLowerCase();
      const bv = (b[sortField] || "").toString().toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [rows, sortField, sortDir]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span style={{ marginLeft: 4, opacity: 0.35, fontSize: 10 }}>⇅</span>;
    return <span style={{ marginLeft: 4, fontSize: 10 }}>{sortDir === "asc" ? "▲" : "▼"}</span>;
  };

  const pages = Math.ceil(total / limit);
  const isView = modal === "view";

  if (denied) return (
    <div className="vendor-page">
      <AccessDenied message={denied.message} required={denied.required} />
    </div>
  );

  return (
    <div className="vendor-page">

      {/* ── Action Bar ── */}
      <div className="vendor-action-bar">
        <button className="new-user-btn" onClick={openAdd}>
          <FiPlus /> New Customer
        </button>
        <div style={{ width: 1, height: 24, background: "#ccc", margin: "0 8px" }} />
        <button className="action-btn view-btn" disabled={selectedIds.length !== 1} onClick={openView}>
          <FiEye /> View
        </button>
        <button className="action-btn edit-btn" disabled={selectedIds.length !== 1} onClick={openEdit}>
          <FiEdit /> Edit
        </button>
        <button className="action-btn delete-btn" disabled={selectedIds.length === 0} onClick={handleDelete}>
          <FiTrash2 /> Delete
        </button>
        {['global_superadmin', 'company_admin'].includes(user?.role) && (
          <>
            <div style={{ width: 1, height: 24, background: "#ccc", margin: "0 8px" }} />
            <button className="action-btn export-btn" onClick={handleExport}>
              <FiRefreshCw size={13} /> Export
            </button>
            <button className="action-btn upload-btn" onClick={() => setIsUploadOpen(true)}>
              <FiUploadCloud /> Upload
            </button>
            <button className="action-btn upload-btn" onClick={() => setIsCoordUploadOpen(true)}>
              <FiUploadCloud /> Upload Coordinators
            </button>
          </>
        )}
        <FilterPanel
          title="Filter customers"
          width={320}
          columns={1}
          values={{ search, country: fCountry, state: fState, status: fStatus }}
          emptyValues={{ search: "", country: "", state: "", status: "" }}
          onChange={(v) => { setSearch(v.search); setFCountry(v.country); setFState(v.state); setFStatus(v.status); setPage(1); }}
        >
          {(filters, set) => (
            <>
              <FilterField label="Search" wide>
                <input autoFocus placeholder="Search customers…" value={filters.search}
                  onChange={(e) => set({ search: e.target.value })} />
              </FilterField>
              <FilterField label="Country">
                <Select
                  options={allCountries.map((c) => ({ value: c.name, label: c.name }))}
                  value={filters.country ? { value: filters.country, label: filters.country } : null}
                  onChange={(s) => set({ country: s ? s.value : "", state: "" })}
                  placeholder="All Countries"
                  isClearable
                  styles={filterSelectStyles}
                />
              </FilterField>
              <FilterField label="State">
                <Select
                  options={statesOf(filters.country).map((s) => ({ value: s.name, label: s.name }))}
                  value={filters.state ? { value: filters.state, label: filters.state } : null}
                  onChange={(s) => set({ state: s ? s.value : "" })}
                  placeholder="All States"
                  isDisabled={!filters.country}
                  isClearable
                  styles={filterSelectStyles}
                />
              </FilterField>
              <FilterField label="Status">
                <Select
                  options={[{ value: "Active", label: "Active" }, { value: "Inactive", label: "Inactive" }]}
                  value={filters.status ? { value: filters.status, label: filters.status } : null}
                  onChange={(s) => set({ status: s ? s.value : "" })}
                  placeholder="All Status"
                  isClearable
                  styles={filterSelectStyles}
                />
              </FilterField>
            </>
          )}
        </FilterPanel>
      </div>

      {/* ── Table Card ── */}
      <div className="management-card">
        <div className="management-header" onClick={() => setIsTableCollapsed((v) => !v)}>
          <div className="header-title">
            <FaUserTie size={18} />
            <h2>Customer Master</h2>
            <span className="vendor-badge">{total}</span>
          </div>
          <div className="header-actions">
            {isTableCollapsed ? <FiChevronDown /> : <FiChevronUp />}
          </div>
        </div>

        {!isTableCollapsed && (
          <>
            <div className="vendor-table-container">
              {loading && rows.length === 0 ? (
                <div className="cust-loading">Loading…</div>
              ) : (
                <table className="vendor-table">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          checked={rows.length > 0 && selectedIds.length === rows.length}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th style={{ width: 52 }}>SR.</th>
                      <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("customerName")}>
                        CUSTOMER NAME <SortIcon field="customerName" />
                      </th>
                      <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("customerCode")}>
                        CODE <SortIcon field="customerCode" />
                      </th>
                      <th>TYPE</th>
                      <th>COUNTRY</th>
                      <th>EMAIL</th>
                      <th>STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="cust-empty-row">No customers found</td>
                      </tr>
                    ) : sortedRows.map((r, i) => (
                      <tr
                        key={r.id || i}
                        className={selectedIds.includes(r.id) ? "selected" : ""}
                        onClick={() => toggleSelectRow(r.id)}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.id)}
                            onChange={() => toggleSelectRow(r.id)}
                          />
                        </td>
                        <td className="cust-td-mono">{r.srNo}</td>
                        <td>{r.customerName}</td>
                        <td className="">{r.customerCode}</td>
                        <td><span className="cust-type-pill">{r.customerType}</span></td>
                        <td>{r.country}</td>
                        <td className="cust-td-muted">{r.email}</td>
                        <td>
                          <span className={`cust-status-pill ${r.status === "Active" ? "cust-status-active" : "cust-status-inactive"}`}>
                            {r.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {total > 0 && (
              <div className="cust-pagination">
                <select
                  value={limit}
                  onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                  style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
                >
                  {[25, 50, 75, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="cust-page-info">Page {page} of {pages}</span>
                <button className="cust-btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
                <button className="cust-btn" disabled={page === pages} onClick={() => setPage((p) => p + 1)}>Next</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Modal ── */}
      {modal && (
        <div className="cust-overlay" onClick={requestCloseModal}>
          <div className="cust-modal" onClick={(e) => e.stopPropagation()}>

            {/* Modal Header */}
            <div className="cust-modal-header">
              <span className="cust-modal-title">{modal.toUpperCase()} CUSTOMER</span>
              <button className="cust-modal-close" onClick={requestCloseModal}>
                <FiX size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="cust-modal-body">
              <form onSubmit={handleSave}>

                {/* Basic Information */}
                <div className="cust-card-wrapper" style={ { display : 'flex' , justifyContent : 'space-between' , gap : '24px'}}>
                <div className="sub-card" style={ { width : '50%'}}>
                  <div className={`sub-card-header ${secBasic ? 'expanded' : ''}`} onClick={() => setSecBasic((v) => !v)}>
                    <h2><FiUser /> BASIC INFORMATION</h2>
                    {secBasic ? <FiChevronUp /> : <FiChevronDown />}
                  </div>
                  {secBasic && (
                    <div className="sub-card-content">
                      <div className="form-grid">
                        <div className="cust-fg-full">
                          <label className="cust-label">CUSTOMER NAME *</label>
                          <input className="cust-input" name="customerName" value={formData.customerName} onChange={handleFormChange} required disabled={isView} placeholder="Enter customer name" />
                        </div>
                        <div>
                          <label className="cust-label">CUSTOMER CODE *</label>
                          <input className="cust-input" name="customerCode" type="text" inputMode="numeric" maxLength="3" pattern="[0-9]{3}" value={formData.customerCode} onChange={handleFormChange} required disabled={isView} placeholder="123" />
                        </div>
                        <div>
                          <label className="cust-label">TYPE</label>
                          <select className="cust-input" name="customerType" value={formData.customerType} onChange={handleFormChange} disabled={isView}>
                            <option value="Direct">Direct</option>
                            <option value="TPI">TPI</option>
                          </select>
                        </div>
                        {modal !== "add" && (
                          <div>
                            <label className="cust-label">STATUS</label>
                            <select className="cust-input" name="status" value={formData.status} onChange={handleFormChange} disabled={isView}>
                              <option value="Active">Active</option>
                              <option value="Inactive">Inactive</option>
                            </select>
                          </div>
                        )}
                        <div>
                          <label className="cust-label">EMAIL</label>
                          <input className="cust-input" name="email" type="email" value={formData.email} onChange={handleFormChange} disabled={isView} placeholder="Enter email" />
                        </div>
                        <div>
                          <PhoneField
                            label="CONTACT NUMBER"
                            value={formData.contactNumber}
                            onChange={(val) => setFormData((f) => ({ ...f, contactNumber: val }))}
                            disabled={isView}
                          />
                        </div>
                        <div>
                          <label className="cust-label">GST / VAT NUMBER</label>
                          <input className="cust-input" name="gstVatNumber" value={formData.gstVatNumber} onChange={handleFormChange} disabled={isView} placeholder="Enter GST/VAT number" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
               

                {/* Location */}
                <div className="sub-card" style={ { width : '50%'}}>
                  <div className={`sub-card-header ${secLocation ? 'expanded' : ''}`} onClick={() => setSecLocation((v) => !v)}>
                    <h2><FiMapPin /> LOCATION</h2>
                    {secLocation ? <FiChevronUp /> : <FiChevronDown />}
                  </div>
                  {secLocation && (
                    <div className="sub-card-content">
                      <div className="form-grid">
                        <div>
                          <label className="cust-label">COUNTRY</label>
                          {isView ? (
                            <input className="cust-input" value={formData.country || ""} disabled />
                          ) : (
                            <Select
                              options={allCountries.map((c) => ({ value: c.name, label: c.name }))}
                              value={formData.country ? { value: formData.country, label: formData.country } : null}
                              onChange={(s) => setFormData((f) => ({ ...f, country: s ? s.value : "", state: "", city: "" }))}
                              placeholder="Select country"
                              isClearable
                              styles={rsStyles}
                            />
                          )}
                        </div>
                        <div>
                          <label className="cust-label">STATE</label>
                          {isView ? (
                            <input className="cust-input" value={formData.state || ""} disabled />
                          ) : (
                            <Select
                              options={stateOptions.map((s) => ({ value: s.name, label: s.name }))}
                              value={formData.state ? { value: formData.state, label: formData.state } : null}
                              onChange={(s) => setFormData((f) => ({ ...f, state: s ? s.value : "", city: "" }))}
                              placeholder="Select state"
                              isDisabled={!formData.country}
                              isClearable
                              styles={rsStyles}
                            />
                          )}
                        </div>
                        <div>
                          <label className="cust-label">CITY</label>
                          {isView ? (
                            <input className="cust-input" value={formData.city || ""} disabled />
                          ) : (
                            <Select
                              options={cityOptions.map((c) => ({ value: c.name, label: c.name }))}
                              value={formData.city ? { value: formData.city, label: formData.city } : null}
                              onChange={(s) => setFormData((f) => ({ ...f, city: s ? s.value : "" }))}
                              placeholder="Select city"
                              isDisabled={!formData.state}
                              isClearable
                              styles={rsStyles}
                            />
                          )}
                        </div>
                        <div className="cust-fg-full">
                          <label className="cust-label">STREET ADDRESS</label>
                          <textarea className="cust-input cust-textarea" name="streetAddress" value={formData.streetAddress} onChange={handleFormChange} required disabled={isView} placeholder="123, Industrial Area, Phase 2" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
               </div>

                {/* Services */}
                <div className="sub-card">
                  {/* <div className={`sub-card-header ${secServices ? 'expanded' : ''}`} onClick={() => setSecServices((v) => !v)}>
                    <h2><FiFileText /> SERVICES</h2>
                    {secServices ? <FiChevronUp /> : <FiChevronDown />}
                  </div> */}
                  {secServices && (
                    <div className="sub-card-content" style={{ padding: 0 , marginBottom : 0 , border:'none'}}>
                      <ServiceSelection 
                        value={formData.services || []}
                        onChange={(val) => setFormData((f) => ({ ...f, services: val }))}
                        readOnly={isView}
                        type="customer"
                      />
                    </div>
                  )}
                </div>

                {/* Coordinators */}
                <div className="sub-card" style={{ marginBottom: 0 }}>
                  <div className={`sub-card-header ${secCoord ? 'expanded' : ''}`} onClick={() => setSecCoord((v) => !v)}>
                    <h2>
                      <FiUsers /> COORDINATORS
                      <span className="cust-coord-count">{formData.coordinators.length}</span>
                    </h2>
                    {secCoord ? <FiChevronUp /> : <FiChevronDown />}
                  </div>

                  {secCoord && (
                    <div>
                      {/* Empty state */}
                      {formData.coordinators.length === 0 && !coordFormOpen && (
                        <div className="cust-coord-empty">
                          <div className="cust-coord-empty-icon">
                            <FiUsers size={16} />
                          </div>
                          <p className="cust-coord-empty-text">No coordinators added yet</p>
                          {!isView && (
                            <button type="button" className="cust-btn cust-btn-green" onClick={openCoordForm}>
                              <FiPlus size={11} /> Add Coordinator
                            </button>
                          )}
                        </div>
                      )}

                      {/* Coordinator table */}
                      {formData.coordinators.length > 0 && (
                        <>
                          <table className="cust-coord-table">
                            <thead>
                              <tr>
                                <th style={{ width: "28%" }}>NAME</th>
                                <th style={{ width: "34%" }}>EMAIL</th>
                                <th style={{ width: "24%" }}>MOBILE</th>
                                {!isView && <th style={{ width: "14%" }}></th>}
                              </tr>
                            </thead>
                            <tbody>
                              {formData.coordinators.map((c, idx) => (
                                <tr key={idx}>
                                  <td>
                                    <div className="cust-nc">
                                      <div className="cust-avatar">{initials(c.name)}</div>
                                      <span className="cust-name">{c.name}</span>
                                    </div>
                                  </td>
                                  <td className="cust-td-muted">{c.email}</td>
                                  <td className="cust-td-muted">{c.mobileNumber || "—"}</td>
                                  {!isView && (
                                    <td>
                                      <button
                                        type="button"
                                        className="cust-del-btn"
                                        onClick={() => handleRemoveCoordinator(idx)}
                                      >
                                        <FiTrash2 size={12} /> Remove
                                      </button>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {!isView && !coordFormOpen && (
                            <div className="cust-coord-footer">
                              <button type="button" className="cust-btn cust-btn-green" onClick={openCoordForm}>
                                <FiPlus size={11} /> Add Coordinator
                              </button>
                            </div>
                          )}
                        </>
                      )}

                      {/* Inline add form */}
                      {coordFormOpen && (
                        <div className="cust-coord-add-panel">
                          <div className="cust-panel-title">
                            <span>NEW COORDINATOR</span>
                            <button type="button" className="cust-cancel-link" onClick={closeCoordForm}>
                              <FiX size={11} /> Cancel
                            </button>
                          </div>
                          <div className="form-grid" style={{ marginBottom: 10 , gridTemplateColumns : "repeat(3,1fr)"}}>
                            <div>
                              <label className="cust-label">NAME *</label>
                              <input
                                className="cust-input"
                                value={coordinatorInput.name}
                                onChange={(e) => setCoordinatorInput((p) => ({ ...p, name: e.target.value }))}
                                placeholder="Enter coordinator name"
                                onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                              />
                            </div>
                            <div>
                              <label className="cust-label">EMAIL *</label>
                              <input
                                className="cust-input"
                                type="email"
                                value={coordinatorInput.email}
                                onChange={(e) => setCoordinatorInput((p) => ({ ...p, email: e.target.value }))}
                                placeholder="Enter email"
                                onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                              />
                            </div>
                            <div>
                              <PhoneField
                                label="MOBILE"
                                value={coordinatorInput.mobileNumber}
                                onChange={(val) => setCoordinatorInput((p) => ({ ...p, mobileNumber: val }))}
                                placeholder="Enter mobile number"
                              />
                            </div>
                          </div>
                          <div className="cust-panel-actions">
                            <button type="button" className="cust-btn" onClick={closeCoordForm}>Cancel</button>
                            <button type="button" className="cust-btn cust-btn-primary" onClick={handleAddCoordinator}>
                              <FiCheck size={11} /> Save Coordinator
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

              </form>
            </div>

            {/* Modal Footer */}
            {!isView && (
              <div className="cust-modal-footer">
                <button type="button" className="cust-btn" onClick={requestCloseModal}>Cancel</button>
                <button type="button" className="cust-btn cust-btn-primary" onClick={handleSave} disabled={saving}>
                  <FiCheck size={11} /> {saving ? "Saving…" : "Save Customer"}
                </button>
              </div>
            )}
            {isView && (
              <div className="cust-modal-footer">
                <button type="button" className="cust-btn" onClick={closeModal}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <MasterUploadModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Upload Customer"
        templateUrl={`${API_URLS.CUSTOMERS}/template`}
        uploadUrl={`${API_URLS.CUSTOMERS}/upload`}
        instructions={[
          "Customer Name and Customer Code are mandatory.",
          "All other fields are optional.",
          "Email must be a valid format if provided."
        ]}
        onSuccess={fetchRows}
        token={token}
      />

      {/* Coordinator Upload Modal */}
      <MasterUploadModal
        isOpen={isCoordUploadOpen}
        onClose={() => setIsCoordUploadOpen(false)}
        title="Upload Coordinators"
        templateUrl={`${API_URLS.CUSTOMERS}/coordinators/template`}
        uploadUrl={`${API_URLS.CUSTOMERS}/coordinators/upload`}
        instructions={[
          "Coordinator Name and Email are mandatory.",
          "Mobile number should include the country code if possible.",
          "Each coordinator must be linked to a valid Customer ID."
        ]}
        onSuccess={fetchRows}
        token={token}
      />
    </div>
  );
};

export default Customers;